import { faDice, faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button, CurrencyEntry, Input, Label, Radios, Well } from '@feathr/components';
import { objectId, tick } from '@feathr/hooks';

import type { IDonationCreatedEvent } from '../../App/FundraisePage/hooks';
import { generateMockDonation } from '../../App/FundraisePage/hooks';
import useDebugDonationEndpoint from './useDebugDonationEndpoint';

import * as styles from './DonationEventForm.css';

export type TDebuggerMode = 'mock' | 'integration';

interface IDonationFormData {
  firstName: string;
  lastName: string;
  amount: number;
  donationType: 'one_time' | 'recurring';
  formName: string;
}

const INITIAL_FORM_DATA: IDonationFormData = {
  firstName: '',
  lastName: '',
  amount: 0,
  donationType: 'one_time',
  formName: '',
};

interface IProps {
  /** The debugger mode - 'mock' publishes locally, 'integration' calls the backend test endpoint. */
  mode: TDebuggerMode;
  /** Callback for mock mode - publishes the donation event locally. */
  onSummon: (donation: IDonationCreatedEvent) => void;
}

/**
 * Form for creating donation events in the debugger.
 * Provides fields for customizing donation data before summoning.
 */
function DonationEventForm({ mode, onSummon }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [formData, setFormData] = useState<IDonationFormData>(INITIAL_FORM_DATA);
  const [isSummoning, setIsSummoning] = useState(false);
  const {
    publishTestEvent,
    isLoading: isBackendLoading,
    error: backendError,
  } = useDebugDonationEndpoint();

  const donationTypeOptions = [
    { id: 'one_time' as const, name: t('One-time') },
    { id: 'recurring' as const, name: t('Recurring') },
  ];

  const handleGenerateMockData = useCallback((): void => {
    const mock = generateMockDonation();
    setFormData({
      firstName: mock.donor_first_name,
      lastName: mock.donor_last_name,
      amount: mock.amount,
      donationType: mock.donation_type,
      formName: mock.form_name,
    });
  }, []);

  const handleFirstNameChange = useCallback((value?: string): void => {
    setFormData((prev) => ({ ...prev, firstName: value ?? '' }));
  }, []);

  const handleLastNameChange = useCallback((value?: string): void => {
    setFormData((prev) => ({ ...prev, lastName: value ?? '' }));
  }, []);

  const handleAmountChange = useCallback((value?: number): void => {
    setFormData((prev) => ({ ...prev, amount: value ?? 0 }));
  }, []);

  const handleDonationTypeChange = useCallback((value?: string): void => {
    if (value === 'one_time' || value === 'recurring') {
      setFormData((prev) => ({ ...prev, donationType: value }));
    }
  }, []);

  const handleFormNameChange = useCallback((value?: string): void => {
    setFormData((prev) => ({ ...prev, formName: value ?? '' }));
  }, []);

  const buildDonationEvent = useCallback((): IDonationCreatedEvent => {
    const formId = objectId();
    return {
      id: objectId(),
      donor_first_name: formData.firstName || 'Anonymous',
      donor_last_name: formData.lastName || 'Donor',
      amount: formData.amount,
      credits_earned: Math.floor(formData.amount * 0.1),
      donation_type: formData.donationType,
      form_id: formId,
      form_name: formData.formName || 'General Donation',
      payment_method:
        crypto.getRandomValues(new Uint32Array(1))[0] % 10 > 7 ? 'ach' : 'credit_card',
      transaction_id: `txn_${objectId()}`,
      created_at: new Date().toISOString(),
    };
  }, [formData]);

  const handleSummon = useCallback(async (): Promise<void> => {
    const donation = buildDonationEvent();

    if (mode === 'integration') {
      await publishTestEvent(donation);
      return;
    }

    // Mock mode: mark as test event before publishing locally
    setIsSummoning(true);
    await tick(300);
    onSummon({ ...donation, isTestEvent: true });
    setIsSummoning(false);
  }, [buildDonationEvent, mode, onSummon, publishTestEvent]);

  const previewName =
    formData.firstName || formData.lastName
      ? `${formData.firstName || 'Anonymous'} ${formData.lastName || 'Donor'}`.trim()
      : t('Anonymous Donor');

  const previewAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(formData.amount);

  return (
    <Well className={styles.formWell}>
      <Stack gap={theme.spacing['4']}>
        <Label>fundraising.donation.created</Label>

        <Stack gap={theme.spacing['3']}>
          <Flex gap={theme.spacing['3']}>
            <Input
              label={t('First name')}
              onChange={handleFirstNameChange}
              placeholder={t('Anonymous')}
              value={formData.firstName}
            />
            <Input
              label={t('Last name')}
              onChange={handleLastNameChange}
              placeholder={t('Donor')}
              value={formData.lastName}
            />
          </Flex>

          <Box>
            <Label>{t('Amount')}</Label>
            <CurrencyEntry onChange={handleAmountChange} value={formData.amount} />
          </Box>

          <Radios
            label={t('Donation type')}
            onChange={handleDonationTypeChange}
            options={donationTypeOptions}
            value={formData.donationType}
          />

          <Input
            label={t('Form name')}
            onChange={handleFormNameChange}
            placeholder={t('General Donation')}
            value={formData.formName}
          />
        </Stack>

        <Box>
          <Label>{t('Preview')}</Label>
          <Well className={styles.preview}>
            <Flex align={'center'} gap={theme.spacing['3']} justify={'space-between'}>
              <span>{previewName}</span>
              <strong className={styles.previewAmount}>{previewAmount}</strong>
            </Flex>
          </Well>
        </Box>

        {backendError && (
          <Alert title={t('Failed to publish')} type={'danger'}>
            {backendError}
          </Alert>
        )}

        <Flex gap={theme.spacing['3']} justify={'flex-end'}>
          <Button onClick={handleGenerateMockData} prefix={<FontAwesomeIcon icon={faDice} />}>
            {t('Randomize')}
          </Button>
          <Button
            isLoading={isSummoning || isBackendLoading}
            onClick={handleSummon}
            prefix={<FontAwesomeIcon icon={faWandMagicSparkles} />}
            type={'primary'}
          >
            {t('Summon')}
          </Button>
        </Flex>
      </Stack>
    </Well>
  );
}

export default DonationEventForm;
